import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { Button } from '../../../Button';
import { type FileUploadObj, getAllowedFileTypes } from '../../index';
import {
  FileUploadBoxContainer,
  FileUploadBrowse,
  FileUploadBrowseSubTitle,
  FileUploadBrowseTitle,
  FileUploadInnerBox,
} from './styles';

type FileUploadBoxProps = {
  wrapperRef: React.RefObject<HTMLDivElement | null>;
  error: string | null;
  filesState: FileUploadObj[];
  maxFiles: number;
  maxFileSizeMb: number;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  handleFileChange: (files: File[]) => void;
  imagesOnly?: boolean;
};

export const FileUploadBox = ({
  wrapperRef,
  error,
  filesState,
  maxFiles,
  fileInputRef,
  imagesOnly = false,
  maxFileSizeMb,
  handleFileChange,
}: FileUploadBoxProps) => {
  const { t } = useTranslation();
  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    const droppedFiles = Array.from(event.dataTransfer.files);

    handleFileChange(droppedFiles);
  };

  const handleBrowseFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);

    handleFileChange(files);
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const allowedTypeReadable = useMemo(
    () =>
      getAllowedFileTypes(imagesOnly)
        .map(({ readable }) => readable)
        .join(', '),
    [imagesOnly],
  );
  const filesToAccept = useMemo(
    () =>
      getAllowedFileTypes(imagesOnly)
        .map(({ fileEnding }) => fileEnding)
        .join(','),
    [imagesOnly],
  );

  return (
    <FileUploadBoxContainer
      ref={wrapperRef}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      hasError={Boolean(error)}
    >
      <FileUploadInnerBox>
        <FileUploadBrowse>
          <FileUploadBrowseTitle>{t('component.fileupload.drop.label')}</FileUploadBrowseTitle>

          <Button
            variant={'text-primary'}
            onClick={handleBrowseClick}
            disabled={filesState.length === maxFiles}
          >
            {t('component.fileupload.drop.browse')}
          </Button>

          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleBrowseFileChange}
            multiple
            accept={filesToAccept}
          />
        </FileUploadBrowse>

        <FileUploadBrowseSubTitle>
          <div>{`${t('component.fileupload.supported.files')}: ${allowedTypeReadable}`}</div>

          <div>{t('component.fileupload.max.files.size', { maxFileSizeMb })}</div>
        </FileUploadBrowseSubTitle>
      </FileUploadInnerBox>
    </FileUploadBoxContainer>
  );
};
